import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { ArrowLeft, Loader2, TrendingUp, TrendingDown, DollarSign, AlertCircle, Home, MapPin, Image, BarChart3, ChevronDown, Plus, X, Sparkles } from 'lucide-react';
import { Footer } from '../components/LegalDisclaimers';
import LanguageSwitcher from '../components/LanguageSwitcher';
import PropertyMap from '../components/PropertyMap';
import NeighborhoodInsights from '../components/NeighborhoodInsights';
import { RendPlexLogo } from '../components/RendPlexLogo';
import DarkModeToggle from '../components/DarkModeToggle';
import AIPropertyChat from '../components/AIPropertyChat';
import { PropertyAdvisorProvider } from '../context/PropertyAdvisorContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PropertyAnalysis() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    property_address: '',
    purchase_price: '350000',
    num_units: '1',
    annual_income: '36000',
    annual_property_tax: '4200',
    down_payment_percent: '20',
    interest_rate: '7.0',
    loan_term_years: '30',
    annual_insurance: '1200',
    monthly_maintenance: '200',
    utilities_electricity: '',
    utilities_heating: '',
    utilities_water: '',
    utilities_sewer: '',
    utilities_internet: '',
    utilities_cable: '',
    vacancy_rate: '5',
    property_management_percent: '10',
    // Additional Operating Expenses — annual amounts. Collapsed by default.
    repairs_reserve_annual: '',
    snow_removal_annual: '',
    landscaping_annual: '',
    cleaning_common_annual: '',
    security_alarm_annual: '',
    garbage_waste_annual: '',
    additional_operating_expenses: [], // [{name, annual_amount}]
  });

  const [showUtilities, setShowUtilities] = useState(false);
  const [showAdditional, setShowAdditional] = useState(false);
  const [propertyImage, setPropertyImage] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);
  // Listing Import™ prefill highlights — the set of field `name` attrs
  // that were just auto-filled. Cleared after ~6 s (matches CSS anim).
  const [prefilledFields, setPrefilledFields] = useState([]);

  // Prefill from Deal Scanner (or any external source) via URL query params
  // OR Listing Import™ (via sessionStorage handoff when source=listing_import).
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (!params.has('source')) return;

    // Path A — Listing Import™ handoff: full payload in sessionStorage.
    if (params.get('source') === 'listing_import') {
      try {
        const raw = window.sessionStorage.getItem('rendplex.listing_import_prefill');
        if (raw) {
          const payload = JSON.parse(raw);
          const filled = [];
          setFormData((prev) => {
            const next = { ...prev };
            Object.entries(payload).forEach(([k, v]) => {
              if (k === 'additional_operating_expenses') {
                if (Array.isArray(v) && v.length) {
                  next[k] = v.map((e) => ({
                    name: String(e.name || ''),
                    annual_amount: Number(e.annual_amount || 0),
                  }));
                  filled.push('additional_operating_expenses');
                }
                return;
              }
              if (v === null || v === undefined || v === '') return;
              next[k] = typeof v === 'number' ? String(v) : v;
              filled.push(k);
            });
            return next;
          });

          // Auto-open collapsed sections if any of their fields were
          // prefilled — so users can see the highlighted values.
          const utilityKeys = [
            'utilities_electricity',
            'utilities_heating',
            'utilities_water',
            'utilities_sewer',
            'utilities_internet',
            'utilities_cable',
          ];
          const additionalKeys = [
            'repairs_reserve_annual',
            'snow_removal_annual',
            'landscaping_annual',
            'cleaning_common_annual',
            'security_alarm_annual',
            'garbage_waste_annual',
            'additional_operating_expenses',
          ];
          if (filled.some((k) => utilityKeys.includes(k))) setShowUtilities(true);
          if (filled.some((k) => additionalKeys.includes(k))) setShowAdditional(true);

          setPrefilledFields(filled);
          window.sessionStorage.removeItem('rendplex.listing_import_prefill');
          toast.success(
            filled.length
              ? t('listingImport.toast.prefilledCount', { count: filled.length })
              : t('listingImport.toast.prefilled'),
          );
        }
      } catch {
        /* ignore malformed payload */
      }
      return;
    }

    // Path B — legacy Deal Scanner / query-string prefill.
    setFormData((prev) => ({
      ...prev,
      property_address: params.get('address') || prev.property_address,
      purchase_price: params.get('price') || prev.purchase_price,
      num_units: params.get('units') || prev.num_units,
      annual_income: params.get('income') || prev.annual_income,
      annual_property_tax: params.get('tax') || prev.annual_property_tax,
      annual_insurance: params.get('insurance') || prev.annual_insurance,
      monthly_maintenance: params.get('maint') || prev.monthly_maintenance,
      down_payment_percent: params.get('dp') || prev.down_payment_percent,
    }));
  }, [location.search, t]);

  // Auto-clear the highlight after the CSS animation finishes.
  useEffect(() => {
    if (!prefilledFields.length) return undefined;
    const id = setTimeout(() => setPrefilledFields([]), 6000);
    return () => clearTimeout(id);
  }, [prefilledFields]);

  // Auto-clear the highlight after the CSS animation finishes.
  useEffect(() => {
    if (!prefilledFields.length) return undefined;
    const id = setTimeout(() => setPrefilledFields([]), 6000);
    return () => clearTimeout(id);
  }, [prefilledFields]);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          withCredentials: true,
        });
        setUser(response.data);
      } catch (error) {
        navigate('/');
      }
    };
    checkAuth();
  }, [navigate]);

  // Debounced property image fetch
  const fetchPropertyImage = useCallback(async (address) => {
    if (!address || address.length < 10) {
      setPropertyImage(null);
      return;
    }
    
    setLoadingImage(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/property/image`, {
        address: address
      }, { withCredentials: true });
      
      setPropertyImage(response.data);
    } catch (error) {
      console.error('Failed to fetch property image:', error);
      setPropertyImage(null);
    } finally {
      setLoadingImage(false);
    }
  }, []);

  // Debounce address changes for image fetch
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.property_address) {
        fetchPropertyImage(formData.property_address);
      }
    }, 1000); // Wait 1 second after typing stops

    return () => clearTimeout(timer);
  }, [formData.property_address, fetchPropertyImage]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSliderChange = (name, value) => {
    setFormData({ ...formData, [name]: value[0].toString() });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        property_address: formData.property_address,
        purchase_price: parseFloat(formData.purchase_price),
        num_units: parseInt(formData.num_units),
        annual_income: parseFloat(formData.annual_income),
        annual_property_tax: parseFloat(formData.annual_property_tax),
        down_payment_percent: parseFloat(formData.down_payment_percent),
        interest_rate: parseFloat(formData.interest_rate),
        loan_term_years: parseInt(formData.loan_term_years),
        annual_insurance: parseFloat(formData.annual_insurance),
        monthly_maintenance: parseFloat(formData.monthly_maintenance),
        utilities_electricity: parseFloat(formData.utilities_electricity) || 0,
        utilities_heating: parseFloat(formData.utilities_heating) || 0,
        utilities_water: parseFloat(formData.utilities_water) || 0,
        utilities_sewer: parseFloat(formData.utilities_sewer) || 0,
        utilities_internet: parseFloat(formData.utilities_internet) || 0,
        utilities_cable: parseFloat(formData.utilities_cable) || 0,
        vacancy_rate: parseFloat(formData.vacancy_rate),
        property_management_percent: parseFloat(formData.property_management_percent),
        // Additional operating expenses (annual). Missing/blank → 0.
        repairs_reserve_annual: parseFloat(formData.repairs_reserve_annual) || 0,
        snow_removal_annual: parseFloat(formData.snow_removal_annual) || 0,
        landscaping_annual: parseFloat(formData.landscaping_annual) || 0,
        cleaning_common_annual: parseFloat(formData.cleaning_common_annual) || 0,
        security_alarm_annual: parseFloat(formData.security_alarm_annual) || 0,
        garbage_waste_annual: parseFloat(formData.garbage_waste_annual) || 0,
        additional_operating_expenses: (formData.additional_operating_expenses || [])
          .filter((e) => (e.name || '').trim() && Number(e.annual_amount) > 0)
          .map((e) => ({ name: e.name.trim(), annual_amount: parseFloat(e.annual_amount) || 0 })),
      };

      const response = await axios.post(`${BACKEND_URL}/api/deals`, payload, {
        withCredentials: true,
      });

      toast.success(t('analyze.analysisComplete'));
      navigate(`/deal/${response.data.deal_id}`);
    } catch (error) {
      console.error('Analysis error:', error);
      if (error.response?.status === 401) {
        // Handled by global interceptor — don't show duplicate toast
      } else if (error.response?.status === 403) {
        toast.error(t('analyze.freeLimitReached'));
        setTimeout(() => navigate('/pricing'), 2000);
      } else {
        toast.error(t('analyze.analysisFailed'));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <PropertyAdvisorProvider
      value={{
        address: formData.property_address,
        purchase_price: formData.purchase_price,
        num_units: formData.num_units,
        annual_income: formData.annual_income,
        down_payment_percent: formData.down_payment_percent,
        interest_rate: formData.interest_rate,
        loan_term_years: formData.loan_term_years,
        annual_property_tax: formData.annual_property_tax,
        annual_insurance: formData.annual_insurance,
        monthly_maintenance: formData.monthly_maintenance,
        vacancy_rate: formData.vacancy_rate,
        property_management_percent: formData.property_management_percent,
      }}
    >
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14] text-foreground transition-colors duration-300">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-[#0B0E14]/70 backdrop-saturate-150 border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <Link to="/dashboard" className="flex items-center gap-2">
              <RendPlexLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              <Button
                data-testid="back-to-dashboard-button"
                onClick={() => navigate('/dashboard')}
                variant="ghost"
                className="text-slate-600"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('nav.backToDashboard')}
              </Button>
              <Button data-testid="new-analysis-button" onClick={() => navigate('/analyze')} size="sm"
                className="bg-[#12355B] hover:bg-[#1565C0] text-white dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#0F172A] font-semibold">
                <Plus className="w-4 h-4 mr-1.5" /> {t('dashboard.newAnalysis')}
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="relative isolate max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        {/* Background Image */}
        <div className="absolute inset-0 -mx-4 sm:-mx-6 lg:-mx-12 overflow-hidden -z-10">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/35251cdc-fe6f-4305-b40a-115bc9637197/images/0d2a9bef8ee372c3f3dd6a13f61ee58191f3855476749614019ce0a2c80d4aec.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#F8FAFC]/[0.94] dark:bg-[#0B0E14]/[0.97] backdrop-blur-[2px]" />
        </div>

        <div className="mb-8">
          {prefilledFields.length > 0 && (
            <>
              <style>{`
                ${prefilledFields
                  .filter((n) => n !== 'additional_operating_expenses')
                  .map((n) => `input[name="${n}"]`)
                  .join(', ') || 'input[data-none]'} {
                  animation: rendplex-prefill-glow 6s ease-out both;
                }
                @keyframes rendplex-prefill-glow {
                  0%   { box-shadow: 0 0 0 0 rgba(69,192,74,0); background-color: transparent; }
                  10%  { box-shadow: 0 0 0 4px rgba(69,192,74,0.35); background-color: rgba(69,192,74,0.08); }
                  70%  { box-shadow: 0 0 0 4px rgba(69,192,74,0.35); background-color: rgba(69,192,74,0.08); }
                  100% { box-shadow: 0 0 0 0 rgba(69,192,74,0); background-color: transparent; }
                }
              `}</style>
              <div
                data-testid="prefill-highlight-banner"
                className="mb-4 rounded-xl border border-[#45C04A]/40 bg-[#45C04A]/10 dark:bg-[#45C04A]/15 px-4 py-3 flex items-center gap-2 text-sm text-[#12355B] dark:text-emerald-100"
              >
                <Sparkles className="w-4 h-4 text-[#45C04A] flex-shrink-0" />
                <span>
                  {t('listingImport.highlight.banner', { count: prefilledFields.length })}
                </span>
              </div>
            </>
          )}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="font-primary font-bold text-4xl text-slate-900 dark:text-slate-100 mb-2">{t('analyze.title')}</h1>
              <p className="text-slate-600 dark:text-slate-400">{t('analyze.subtitle')}</p>
            </div>
            <Button
              data-testid="listing-import-cta"
              onClick={() => navigate('/listing-import')}
              className="rounded-xl bg-gradient-to-r from-[#45C04A] to-[#65D95A] hover:from-[#65D95A] hover:to-[#45C04A] text-[#12355B] font-semibold shadow-md"
            >
              <Sparkles className="w-4 h-4 mr-1.5" />
              {t('listingImport.cta')}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form - Left Side */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8">
              {/* Property Info */}
              <div className="mb-8">
                <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <Home className="w-5 h-5 text-[#45C04A]" />
                  {t('analyze.propertyInfo')}
                </h2>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="property_address">{t('analyze.propertyAddress')} *</Label>
                    <Input
                      data-testid="property-address-input"
                      id="property_address"
                      name="property_address"
                      value={formData.property_address}
                      onChange={handleChange}
                      placeholder={t('analyze.propertyAddressPlaceholder')}
                      required
                      className="rounded-2xl"
                    />
                  </div>
                  {/* Property Image & Map Preview */}
                  {(loadingImage || propertyImage) && (
                    <div data-testid="property-image-preview">
                      {loadingImage ? (
                        <div className="flex items-center justify-center h-48 bg-slate-50 dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
                          <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span className="text-sm">{t('analyze.loadingImage')}</span>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {/* Street View or Map */}
                            {propertyImage?.image_url ? (
                              <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 relative">
                                <img
                                  src={propertyImage.image_url}
                                  alt={formData.property_address}
                                  className="w-full h-48 object-cover"
                                  onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                                />
                                <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                  <MapPin className="w-3 h-3" />
                                  {t('analyze.streetViewAvailable')}
                                </div>
                                <div
                                  data-testid="street-view-address-label"
                                  className="absolute top-2 left-2 right-2 flex items-start gap-1.5 bg-white/95 dark:bg-[#0B0E14]/95 text-slate-800 dark:text-slate-100 text-[11px] px-2 py-1 rounded shadow-sm pointer-events-none"
                                >
                                  <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0 text-[#45C04A]" />
                                  <span className="font-semibold break-words leading-tight">{formData.property_address}</span>
                                </div>
                              </div>
                            ) : propertyImage?.latitude && propertyImage?.longitude ? (
                              <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-700 relative">
                                <PropertyMap
                                  latitude={propertyImage.latitude}
                                  longitude={propertyImage.longitude}
                                  address={formData.property_address}
                                  height="192px"
                                />
                                <div className="absolute bottom-2 right-2 flex items-center gap-1.5 bg-black/60 text-white text-xs px-2 py-1 rounded z-[999]">
                                  <MapPin className="w-3 h-3" />
                                  {t('analyze.mapView')}
                                </div>
                              </div>
                            ) : null}
                            {/* Second map column */}
                            {propertyImage?.latitude && propertyImage?.longitude && propertyImage?.image_url && (
                              <PropertyMap
                                latitude={propertyImage.latitude}
                                longitude={propertyImage.longitude}
                                address={formData.property_address}
                                height="192px"
                              />
                            )}
                          </div>
                          {/* Lazy-load Street View button */}
                          {!propertyImage?.image_url && propertyImage?.latitude && (
                            <button
                              type="button"
                              data-testid="load-streetview-btn"
                              onClick={async () => {
                                setLoadingImage(true);
                                try {
                                  const resp = await axios.post(`${BACKEND_URL}/api/property/streetview`, {
                                    address: formData.property_address
                                  });
                                  if (resp.data?.image_url) {
                                    setPropertyImage(prev => ({ ...prev, image_url: resp.data.image_url, image_type: 'street_view' }));
                                  } else {
                                    toast.info(t('analyze.noStreetView'));
                                  }
                                } catch { /* ignore */ }
                                setLoadingImage(false);
                              }}
                              className="w-full text-center py-2 text-sm font-medium text-[#12355B] dark:text-[#45C04A] bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                            >
                              <Image className="w-4 h-4 inline mr-2" />
                              {t('analyze.loadStreetView')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="purchase_price">{t('analyze.purchasePrice')} *</Label>
                      <Input
                        data-testid="purchase-price-input"
                        id="purchase_price"
                        name="purchase_price"
                        type="number"
                        step="1000"
                        value={formData.purchase_price}
                        onChange={handleChange}
                        required
                        className="rounded-2xl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="num_units">{t('analyze.numUnits')} *</Label>
                      <Input
                        data-testid="num-units-input"
                        id="num_units"
                        name="num_units"
                        type="number"
                        value={formData.num_units}
                        onChange={handleChange}
                        required
                        className="rounded-2xl"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="annual_income">{t('analyze.annualIncome')} *</Label>
                    <Input
                      data-testid="annual-income-input"
                      id="annual_income"
                      name="annual_income"
                      type="number"
                      step="100"
                      value={formData.annual_income}
                      onChange={handleChange}
                      placeholder="36000"
                      required
                      className="rounded-2xl"
                    />
                    <p className="text-xs text-slate-500 mt-1">{t('analyze.annualIncomeHelp')}</p>
                  </div>
                </div>
              </div>

              {/* Financing with Sliders */}
              <div className="mb-8">
                <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100 mb-4 flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#45C04A]" />
                  {t('analyze.financing')}
                </h2>
                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>{t('analyze.downPayment')}</Label>
                      <span className="text-sm font-medium text-[#45C04A]">{formData.down_payment_percent}%</span>
                    </div>
                    <Slider
                      value={[parseFloat(formData.down_payment_percent)]}
                      onValueChange={(value) => handleSliderChange('down_payment_percent', value)}
                      min={5}
                      max={50}
                      step={1}
                      className="mb-1"
                    />
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>5%</span>
                      <span>50%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>{t('analyze.interestRate')}</Label>
                      <span className="text-sm font-medium text-[#45C04A]">{formData.interest_rate}%</span>
                    </div>
                    <Slider
                      value={[parseFloat(formData.interest_rate)]}
                      onValueChange={(value) => handleSliderChange('interest_rate', value)}
                      min={3}
                      max={12}
                      step={0.25}
                      className="mb-1"
                    />
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>3%</span>
                      <span>12%</span>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="loan_term_years">{t('analyze.loanTerm')}</Label>
                    <Input
                      id="loan_term_years"
                      name="loan_term_years"
                      type="number"
                      value={formData.loan_term_years}
                      onChange={handleChange}
                      className="rounded-2xl"
                    />
                  </div>
                </div>
              </div>

              {/* Operating Expenses with Sliders */}
              <div className="mb-8">
                <h2 className="font-primary font-bold text-xl text-slate-900 dark:text-slate-100 mb-4">{t('analyze.operatingExpenses')}</h2>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="annual_property_tax">{t('analyze.annualPropertyTax')} *</Label>
                      <Input
                        data-testid="property-tax-input"
                        id="annual_property_tax"
                        name="annual_property_tax"
                        type="number"
                        step="100"
                        value={formData.annual_property_tax}
                        onChange={handleChange}
                        required
                        className="rounded-2xl"
                      />
                    </div>
                    <div>
                      <Label htmlFor="annual_insurance">{t('analyze.annualInsurance')}</Label>
                      <Input
                        id="annual_insurance"
                        name="annual_insurance"
                        type="number"
                        step="100"
                        value={formData.annual_insurance}
                        onChange={handleChange}
                        className="rounded-2xl"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="monthly_maintenance">{t('analyze.monthlyMaintenance')}</Label>
                    <Input
                      id="monthly_maintenance"
                      name="monthly_maintenance"
                      type="number"
                      step="10"
                      value={formData.monthly_maintenance}
                      onChange={handleChange}
                      className="rounded-2xl"
                    />
                  </div>

                  {/* Utilities (if paid by owner) */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-4" data-testid="utilities-section">
                    <button
                      type="button"
                      onClick={() => setShowUtilities(!showUtilities)}
                      className="w-full flex items-center justify-between text-left"
                      data-testid="utilities-toggle"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('analyze.utilitiesTitle')}</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">{t('analyze.utilitiesHint')}</p>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showUtilities ? 'rotate-180' : ''}`} />
                    </button>
                    {showUtilities && (
                      <div className="grid grid-cols-2 gap-3 mt-4" data-testid="utilities-fields">
                        <div>
                          <Label htmlFor="utilities_electricity" className="text-xs">{t('analyze.electricity')}</Label>
                          <Input id="utilities_electricity" name="utilities_electricity" type="number" step="5" placeholder="0" value={formData.utilities_electricity} onChange={handleChange} className="rounded-2xl" />
                        </div>
                        <div>
                          <Label htmlFor="utilities_heating" className="text-xs">{t('analyze.heating')}</Label>
                          <Input id="utilities_heating" name="utilities_heating" type="number" step="5" placeholder="0" value={formData.utilities_heating} onChange={handleChange} className="rounded-2xl" />
                        </div>
                        <div>
                          <Label htmlFor="utilities_water" className="text-xs">{t('analyze.water')}</Label>
                          <Input id="utilities_water" name="utilities_water" type="number" step="5" placeholder="0" value={formData.utilities_water} onChange={handleChange} className="rounded-2xl" />
                        </div>
                        <div>
                          <Label htmlFor="utilities_sewer" className="text-xs">{t('analyze.sewer')}</Label>
                          <Input id="utilities_sewer" name="utilities_sewer" type="number" step="5" placeholder="0" value={formData.utilities_sewer} onChange={handleChange} className="rounded-2xl" />
                        </div>
                        <div>
                          <Label htmlFor="utilities_internet" className="text-xs">{t('analyze.internet')}</Label>
                          <Input id="utilities_internet" name="utilities_internet" type="number" step="5" placeholder="0" value={formData.utilities_internet} onChange={handleChange} className="rounded-2xl" />
                        </div>
                        <div>
                          <Label htmlFor="utilities_cable" className="text-xs">{t('analyze.cable')}</Label>
                          <Input id="utilities_cable" name="utilities_cable" type="number" step="5" placeholder="0" value={formData.utilities_cable} onChange={handleChange} className="rounded-2xl" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Additional Operating Expenses (optional, collapsed by default) */}
                  <div className="border border-slate-200 dark:border-slate-700 rounded-2xl p-4" data-testid="additional-expenses-section">
                    <button
                      type="button"
                      onClick={() => setShowAdditional(!showAdditional)}
                      className="w-full flex items-center justify-between text-left"
                      data-testid="additional-expenses-toggle"
                    >
                      <div>
                        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t('analyze.additionalTitle')}</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500">{t('analyze.additionalHint')}</p>
                      </div>
                      <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showAdditional ? 'rotate-180' : ''}`} />
                    </button>
                    {showAdditional && (
                      <div className="mt-4 space-y-3" data-testid="additional-expenses-fields">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {[
                            { key: 'repairs_reserve_annual', label: t('analyze.repairsReserve') },
                            { key: 'snow_removal_annual', label: t('analyze.snowRemoval') },
                            { key: 'landscaping_annual', label: t('analyze.landscaping') },
                            { key: 'cleaning_common_annual', label: t('analyze.cleaningCommon') },
                            { key: 'security_alarm_annual', label: t('analyze.securityAlarm') },
                            { key: 'garbage_waste_annual', label: t('analyze.garbageWaste') },
                          ].map((f) => (
                            <div key={f.key}>
                              <Label htmlFor={f.key} className="text-xs">{f.label} ({t('analyze.annualDollars')})</Label>
                              <Input
                                id={f.key}
                                name={f.key}
                                data-testid={`additional-${f.key}`}
                                type="number"
                                step="50"
                                placeholder="0"
                                value={formData[f.key]}
                                onChange={handleChange}
                                className="rounded-2xl"
                              />
                            </div>
                          ))}
                        </div>

                        {/* Custom expenses */}
                        <div className="pt-3 border-t border-slate-100 dark:border-slate-800 space-y-2">
                          <p className="text-xs font-semibold text-slate-500 dark:text-slate-400">{t('analyze.customExpensesTitle')}</p>
                          {(formData.additional_operating_expenses || []).map((exp, idx) => (
                            <div key={idx} className="flex items-center gap-2" data-testid={`custom-expense-row-${idx}`}>
                              <Input
                                data-testid={`custom-expense-name-${idx}`}
                                type="text"
                                placeholder={t('analyze.customExpensePlaceholder')}
                                value={exp.name}
                                onChange={(e) => {
                                  const next = [...formData.additional_operating_expenses];
                                  next[idx] = { ...next[idx], name: e.target.value };
                                  setFormData({ ...formData, additional_operating_expenses: next });
                                }}
                                className="rounded-2xl flex-1"
                              />
                              <Input
                                data-testid={`custom-expense-amount-${idx}`}
                                type="number"
                                step="50"
                                placeholder={t('analyze.annualDollars')}
                                value={exp.annual_amount}
                                onChange={(e) => {
                                  const next = [...formData.additional_operating_expenses];
                                  next[idx] = { ...next[idx], annual_amount: e.target.value };
                                  setFormData({ ...formData, additional_operating_expenses: next });
                                }}
                                className="rounded-2xl w-32"
                              />
                              <Button
                                type="button"
                                data-testid={`custom-expense-remove-${idx}`}
                                onClick={() => {
                                  const next = formData.additional_operating_expenses.filter((_, i) => i !== idx);
                                  setFormData({ ...formData, additional_operating_expenses: next });
                                }}
                                variant="ghost"
                                size="sm"
                                className="text-slate-400 hover:text-red-500 h-9 w-9 p-0"
                                aria-label={t('analyze.removeExpense')}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                          <button
                            type="button"
                            data-testid="add-custom-expense-btn"
                            onClick={() => {
                              setFormData({
                                ...formData,
                                additional_operating_expenses: [
                                  ...(formData.additional_operating_expenses || []),
                                  { name: '', annual_amount: '' },
                                ],
                              });
                            }}
                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#12355B] dark:text-[#45C04A] hover:underline"
                          >
                            <Plus className="w-3.5 h-3.5" /> {t('analyze.addAnotherExpense')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>{t('analyze.vacancyRate')}</Label>
                      <span className="text-sm font-medium text-[#45C04A]">{formData.vacancy_rate}%</span>
                    </div>
                    <Slider
                      value={[parseFloat(formData.vacancy_rate)]}
                      onValueChange={(value) => handleSliderChange('vacancy_rate', value)}
                      min={0}
                      max={20}
                      step={1}
                      className="mb-1"
                    />
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>0%</span>
                      <span>20%</span>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <Label>{t('analyze.propertyManagement')}</Label>
                      <span className="text-sm font-medium text-[#45C04A]">{formData.property_management_percent}%</span>
                    </div>
                    <Slider
                      value={[parseFloat(formData.property_management_percent)]}
                      onValueChange={(value) => handleSliderChange('property_management_percent', value)}
                      min={0}
                      max={15}
                      step={0.5}
                      className="mb-1"
                    />
                    <div className="flex justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>0%</span>
                      <span>15%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-4">
                <Button
                  data-testid="cancel-button"
                  type="button"
                  onClick={() => navigate('/dashboard')}
                  className="rounded-2xl bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600"
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  data-testid="analyze-submit-button"
                  type="submit"
                  disabled={loading}
                  className="bg-[#12355B] hover:bg-[#12355B]/90 dark:bg-[#45C04A] dark:hover:bg-[#65D95A] dark:text-[#12355B] text-white rounded-2xl px-8"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('analyze.analyzing')}
                    </>
                  ) : (
                    t('analyze.saveAnalyze')
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* Neighborhood Insights - Right Side */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              {/* Neighborhood Insights - Shows when coordinates are available */}
              {propertyImage?.latitude && propertyImage?.longitude && (
                <div className="mt-4">
                  <NeighborhoodInsights
                    latitude={propertyImage.latitude}
                    longitude={propertyImage.longitude}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />

      {/* Floating RendPleX AI Advisor — reads property from PropertyAdvisorProvider */}
      <AIPropertyChat />
    </div>
    </PropertyAdvisorProvider>
  );
}
